(() => {
  const FILTER_PREFIX = 'filter.';

  // Keep duplicated filter inputs (pills / drawer) in sync so merged form data stays correct
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input.name || input.name.indexOf(FILTER_PREFIX) !== 0) return;
      if (!input.closest('facet-filters-form')) return;

      const selector = `facet-filters-form [name="${CSS.escape(input.name)}"]`;
      document.querySelectorAll(selector).forEach((other) => {
        if (other === input) return;
        if (input.type === 'checkbox') {
          if (other.value === input.value) other.checked = input.checked;
        } else {
          other.value = input.value;
        }
      });
    },
    true
  );

  // Filter pills: only one open at a time, close on outside click / Escape
  const closePills = (except) => {
    document.querySelectorAll('details.facets__disclosure[open]').forEach((details) => {
      if (details !== except) details.removeAttribute('open');
    });
  };

  let drawerOpenTimer = 0;
  let drawerOpener = null;

  const syncDrawerButtons = (drawer) => {
    if (!drawer) return;
    const expanded = drawer.open && drawer.classList.contains('menu-opening');
    document.querySelectorAll('.js-open-filters-drawer').forEach((button) => {
      if (button.getAttribute('aria-controls') === drawer.id) button.setAttribute('aria-expanded', expanded);
    });
  };

  const openAllFiltersPanel = (button) => {
    clearTimeout(drawerOpenTimer);
    const drawerId = button.getAttribute('aria-controls');
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;
    drawerOpener = button;

    const openPanel = () => {
      drawerOpenTimer = 0;
      const currentDrawer = document.getElementById(drawerId);
      const summary = currentDrawer && currentDrawer.querySelector('summary');
      if (summary && !currentDrawer.open) summary.click();
      syncDrawerButtons(currentDrawer);
    };

    if (drawer.open && !drawer.classList.contains('menu-opening')) {
      drawerOpenTimer = setTimeout(openPanel, 550);
    } else {
      openPanel();
    }
  };

  const closeAllFiltersPanel = () => {
    clearTimeout(drawerOpenTimer);
    drawerOpenTimer = 0;
    const openDrawer = document.querySelector('.collection-facets--bar .facets__drawer details[open]');
    if (!openDrawer) return;
    const closeBtn = openDrawer.querySelector('.js-btn-close-drawer');
    if (closeBtn) closeBtn.click();
    else openDrawer.removeAttribute('open');
    syncDrawerButtons(openDrawer);
  };

  document.addEventListener('click', (event) => {
    const disclosure = event.target.closest('details.facets__disclosure');
    closePills(disclosure);

    const allFiltersBtn = event.target.closest('.js-open-filters-drawer');
    if (allFiltersBtn) {
      event.preventDefault();
      openAllFiltersPanel(allFiltersBtn);
      return;
    }

    const closeButton = event.target.closest('.collection-facets--bar .js-btn-close-drawer');
    if (closeButton) {
      clearTimeout(drawerOpenTimer);
      drawerOpenTimer = 0;
      syncDrawerButtons(closeButton.closest('details.drawer__container'));
    }

    // Close the all-filters panel when clicking on the backdrop (outside the panel)
    const openDrawer = document.querySelector('.collection-facets--bar .facets__drawer details[open]');
    if (!openDrawer) return;
    const content = openDrawer.querySelector('.drawer__content');
    const summary = openDrawer.querySelector('summary');
    const insidePanel = content && content.contains(event.target);
    const onSummary = summary && summary.contains(event.target);
    if (!insidePanel && !onSummary) closeAllFiltersPanel();
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.classList) return;

      if (details.classList.contains('facets__disclosure') && details.open) {
        closePills(details);
        const panel = details.querySelector('.facets__dropdown');
        if (panel) {
          details.classList.remove('facets__disclosure--right');
          const rect = panel.getBoundingClientRect();
          if (rect.right > window.innerWidth - 16) details.classList.add('facets__disclosure--right');
        }
      }

      // The theme's drawer waits for a transitionend that never fires on our fixed
      // desktop panel, leaving 'drawer-transitioning' (and its dim overlay) stuck.
      if (details.classList.contains('drawer__container') && details.closest('.collection-facets--bar')) {
        syncDrawerButtons(details);
        if (!details.open && drawerOpener && drawerOpener.isConnected && details.contains(document.activeElement)) {
          drawerOpener.focus({ preventScroll: true });
        }
        setTimeout(() => details.classList.remove('drawer-transitioning'), 400);
      }
    },
    true
  );

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      closePills(null);
      closeAllFiltersPanel();
    }
  });

  // Search inside long facet value lists (client-side, no fetch)
  document.addEventListener(
    'input',
    (event) => {
      const field = event.target.closest && event.target.closest('.js-facet-search');
      if (!field) return;
      event.stopPropagation();
      const scope = field.closest('.js-filter');
      if (!scope) return;
      const query = field.value.trim().toLowerCase();
      scope.querySelectorAll('.facets__item').forEach((item) => {
        const label = item.getAttribute('data-facet-label') || '';
        item.style.display = query === '' || label.indexOf(query) !== -1 ? '' : 'none';
      });
    },
    true
  );

  // Dual-handle range slider for price / dimension facets
  class PriceRangeSlider extends HTMLElement {
    connectedCallback() {
      this.minRange = this.querySelector('.js-range-min');
      this.maxRange = this.querySelector('.js-range-max');
      this.minNum = this.querySelector('.js-range-min-num');
      this.maxNum = this.querySelector('.js-range-max-num');
      this.progress = this.querySelector('.facets__range-progress');
      if (!this.minRange || !this.maxRange || !this.minNum || !this.maxNum) return;
      this.rangeMax = parseInt(this.dataset.rangeMax, 10) || parseInt(this.maxRange.max, 10) || 0;

      [this.minRange, this.maxRange].forEach((input) => {
        input.addEventListener('input', (e) => this.onSlide(e));
        input.addEventListener('change', () => this.commit());
      });
      [this.minNum, this.maxNum].forEach((input) => {
        input.addEventListener('change', () => this.syncFromNumbers());
      });
      this.paint();
    }

    values() {
      const lo = parseInt(this.minRange.value, 10) || 0;
      const parsedHi = parseInt(this.maxRange.value, 10);
      const hi = isNaN(parsedHi) ? this.rangeMax : parsedHi;
      return [lo, hi];
    }

    onSlide(event) {
      const [lo, hi] = this.values();
      if (hi - lo < 1) {
        if (event.target === this.minRange) this.minRange.value = Math.max(hi - 1, 0);
        else this.maxRange.value = Math.min(lo + 1, this.rangeMax);
      }
      this.paint();
    }

    paint() {
      if (!this.progress || this.rangeMax <= 0) return;
      const [lo, hi] = this.values();
      this.progress.style.left = `${(lo / this.rangeMax) * 100}%`;
      this.progress.style.right = `${100 - (hi / this.rangeMax) * 100}%`;
    }

    commit() {
      const [lo, hi] = this.values();
      this.minNum.value = lo > 0 ? lo : '';
      this.maxNum.value = hi < this.rangeMax ? hi : '';
      this.minNum.dispatchEvent(new Event('change', { bubbles: true }));
      this.maxNum.dispatchEvent(new Event('change', { bubbles: true }));
      this.minNum.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromNumbers() {
      const lo = parseInt(this.minNum.value, 10);
      const hi = parseInt(this.maxNum.value, 10);
      this.minRange.value = isNaN(lo) ? 0 : Math.min(lo, this.rangeMax);
      this.maxRange.value = isNaN(hi) ? this.rangeMax : Math.min(hi, this.rangeMax);
      this.paint();
    }
  }
  if (!customElements.get('price-range-slider')) customElements.define('price-range-slider', PriceRangeSlider);

  // Keep the sticky filter bar below a sticky/fixed header
  const setStickyOffset = () => {
    let offset = 0;
    document
      .querySelectorAll('#shopify-section-header, .section-header, .shopify-section-group-header-group, header')
      .forEach((el) => {
        const style = getComputedStyle(el);
        if (style.position === 'sticky' || style.position === 'fixed') {
          offset = Math.max(offset, el.getBoundingClientRect().height);
        }
      });
    document.documentElement.style.setProperty('--facets-bar-offset', `${Math.round(offset)}px`);
  };
  window.addEventListener('load', setStickyOffset);
  window.addEventListener('resize', () => {
    clearTimeout(window.__facetsBarOffsetTimer);
    window.__facetsBarOffsetTimer = setTimeout(setStickyOffset, 200);
  });
  setStickyOffset();
})();
